import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/format";
import {
    Globe2, RefreshCw, KeyRound, Zap, AlertCircle, CheckCircle2, XCircle, Save, Link2, DollarSign, Activity, Package, Bell, Send, History, Clock,
} from "lucide-react";

const OTA_META = {
    booking: { label: "Booking.com", color: "bg-blue-500" },
    airbnb:  { label: "Airbnb",      color: "bg-pink-500" },
    expedia: { label: "Expedia",     color: "bg-amber-500" },
    agoda:   { label: "Agoda",       color: "bg-red-500" },
};

export default function SuperChannex() {
    const [cfg, setCfg] = useState(null);
    const [hotels, setHotels] = useState([]);
    const [monitor, setMonitor] = useState(null);
    const [health, setHealth] = useState(null);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({ api_key: "", environment: "staging", webhook_secret: "" });

    const load = async () => {
        try {
            const [c, h, m, ha] = await Promise.all([
                api.get("/channex/config"),
                api.get("/channex/hotels"),
                api.get("/channex/monitoring"),
                api.get("/channex/health-alert/status").catch(() => ({ data: null })),
            ]);
            setCfg(c.data);
            setHotels(h.data);
            setMonitor(m.data);
            setHealth(ha.data);
            setForm((f) => ({ ...f, environment: c.data.environment || "staging" }));
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    useEffect(() => { load(); }, []);

    const saveConfig = async () => {
        setBusy(true);
        try {
            const body = { environment: form.environment };
            if (form.api_key) body.api_key = form.api_key;
            if (form.webhook_secret) body.webhook_secret = form.webhook_secret;
            await api.put("/channex/config", body);
            toast.success("Distribution config saved");
            setForm({ ...form, api_key: "", webhook_secret: "" });
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const testConnection = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/channex/config/test");
            if (data.status === "healthy") toast.success("Connection healthy ✓");
            else toast.error(`Status ${data.status}: ${data.error || "unknown"}`);
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const pollRevisions = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/channex/poll-revision-feed");
            toast.success(`Feed: ${data.processed} processed · ${data.acked} acked · ${data.failed} failed`);
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const webhookUrl = `${window.location.origin}/api/channex/webhook`;

    return (
        <>
            <Topbar title="Distribution (Channel Manager)" subtitle="Global Channex configuration + per-hotel bindings" />
            <div className="flex-1 overflow-auto p-6 space-y-6">
                {/* Monitoring */}
                {monitor && (
                    <div className="grid gap-3 sm:grid-cols-4">
                        <StatCard label="Webhooks 24h" value={monitor.webhooks_24h.total} tone="blue" />
                        <StatCard label="Success rate" value={monitor.webhooks_24h.success_rate !== null ? `${monitor.webhooks_24h.success_rate}%` : "—"} tone="emerald" />
                        <StatCard label="Failed" value={monitor.webhooks_24h.failed} tone={monitor.webhooks_24h.failed > 0 ? "red" : "slate"} />
                        <StatCard label="Unmapped bookings" value={monitor.unmapped_bookings} tone={monitor.unmapped_bookings > 0 ? "amber" : "slate"} />
                    </div>
                )}

                {/* Delta-push outbox observability (P1) */}
                <OutboxDashboard />

                {/* Escalation config (Slack + PagerDuty) */}
                <EscalationConfigCard />

                {/* Incident timeline (last few stuck-outbox events) */}
                <IncidentTimeline />

                {/* Channex Health Dashboard */}
                <div className="grid gap-6 lg:grid-cols-3" data-testid="channex-health-dashboard">
                    {/* Left: Health alert config */}
                    <div className="akm-card p-5" data-testid="channex-health-alert">
                        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                            <Activity size={12} /> Health Alerts
                        </div>
                        <h3 className="mt-1 text-base font-semibold text-slate-900">Auto-monitoring</h3>
                        {health ? (
                            <div className="mt-3 space-y-2 text-[12px]">
                                <Row label="Threshold" value={`${health.config?.threshold_pct}% success`} />
                                <Row label="Window" value={`${Math.round((health.config?.window_seconds || 0) / 60)} min`} />
                                <Row label="Check every" value={`${Math.round((health.config?.check_interval_seconds || 0) / 60)} min`} />
                                <Row label="Cooldown" value={`${Math.round((health.config?.cooldown_seconds || 0) / 60)} min`} />
                                <Row label="Alerts to" value={(health.config?.recipients || []).join(", ") || "—"} />
                                <div className="mt-3 pt-3 border-t border-slate-100">
                                    <Row label="Current success rate" value={
                                        health.current_health?.success_rate !== null
                                            ? `${health.current_health.success_rate}%`
                                            : "No data"
                                    } />
                                    <Row label="Last alert" value={health.last_alert?.sent_at ? formatDateTime(health.last_alert.sent_at) : "Never"} />
                                </div>
                                <button
                                    onClick={async () => {
                                        setBusy(true);
                                        try {
                                            const { data } = await api.post("/channex/health-alert/check");
                                            toast.success(`Check run: ${data.status || "ok"}`);
                                            await load();
                                        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
                                        finally { setBusy(false); }
                                    }}
                                    disabled={busy}
                                    className="akm-btn-ghost mt-2 w-full text-[11px]"
                                    data-testid="channex-health-check-now">
                                    <RefreshCw size={11} /> Run health check now
                                </button>
                            </div>
                        ) : (
                            <div className="mt-3 text-[12px] text-slate-500">Loading…</div>
                        )}
                    </div>

                    {/* Middle: Recent events */}
                    <div className="akm-card p-5 lg:col-span-2" data-testid="channex-recent-events">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                                    <Zap size={12} className="inline -mt-0.5 mr-1" /> Recent webhook events
                                </div>
                                <h3 className="mt-1 text-base font-semibold text-slate-900">Last {(monitor?.recent_events || []).length} events</h3>
                            </div>
                            <button onClick={pollRevisions} disabled={busy} className="akm-btn-ghost text-[11px]"
                                    data-testid="channex-poll-revisions">
                                <RefreshCw size={11} /> Poll revision feed
                            </button>
                        </div>
                        <div className="mt-4 max-h-[320px] overflow-y-auto">
                            {(monitor?.recent_events || []).slice(0, 15).map((e) => (
                                <div key={e.id} className="flex items-center gap-3 border-b border-slate-100 py-2 text-[12px]"
                                     data-testid={`event-${e.event_type}`}>
                                    {e.processed ? <CheckCircle2 size={13} className="text-emerald-500 shrink-0" />
                                                 : e.error ? <XCircle size={13} className="text-red-500 shrink-0" />
                                                           : <RefreshCw size={13} className="text-amber-500 shrink-0" />}
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-baseline gap-2">
                                            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-700">
                                                {e.event_type?.replace("booking_", "") || "event"}
                                            </span>
                                            <span className="truncate font-mono text-[10.5px] text-slate-500">{e.booking_id}</span>
                                        </div>
                                        {e.error && <div className="mt-0.5 text-[10px] text-red-600">{e.error}</div>}
                                    </div>
                                    <span className="shrink-0 text-[10px] text-slate-400">{formatDateTime(e.received_at)}</span>
                                </div>
                            ))}
                            {(!monitor?.recent_events || monitor.recent_events.length === 0) && (
                                <div className="py-8 text-center text-[12px] text-slate-500">No webhook events yet.</div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Certification submission link */}
                <div className="akm-card p-5 border-primary/30 bg-gradient-to-br from-orange-50 to-white" data-testid="channex-certification">
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-primary">
                                <CheckCircle2 size={12} /> Partner Certification
                            </div>
                            <h3 className="mt-1 text-base font-semibold text-slate-900">Channex Submission Dossier</h3>
                            <p className="mt-1 text-[12px] text-slate-600">
                                Full technical submission PDF (v1.2 · 6 pages) ready to send to the Channex partner team.
                                Contains flow diagrams, endpoint inventory, retry/ack policies, and staging test results.
                            </p>
                        </div>
                        <a href="/downloads/Akomoda_Channex_Certification.pdf"
                           target="_blank" rel="noreferrer"
                           className="akm-btn-primary text-[12px] shrink-0"
                           data-testid="download-channex-certification">
                            <Save size={12} /> Download PDF
                        </a>
                    </div>
                </div>

                {/* Global config */}
                <div className="akm-card p-5">
                    <div className="mb-4 flex items-start justify-between">
                        <div>
                            <div className="text-[13px] font-semibold tracking-tight">Global connection</div>
                            <div className="mt-1 text-[12px] text-muted-foreground">Only Super Admin sees this. Hotels see just their connected OTAs.</div>
                        </div>
                        <div className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                            cfg?.status === "healthy"
                                ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
                                : cfg?.status === "degraded"
                                    ? "bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300"
                                    : "bg-slate-100 text-slate-600 dark:bg-slate-500/20 dark:text-slate-300"}`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${
                                cfg?.status === "healthy" ? "bg-emerald-500" : cfg?.status === "degraded" ? "bg-amber-500" : "bg-slate-400"}`} />
                            {cfg?.status || "unconfigured"}
                        </div>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                            <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Environment</label>
                            <select value={form.environment}
                                    onChange={(e) => setForm({ ...form, environment: e.target.value })}
                                    className="akm-input mt-1 text-[13px]" data-testid="channex-env">
                                <option value="staging">Staging (test)</option>
                                <option value="production">Production (live)</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                                API key {cfg?.api_key_present && <span className="text-emerald-600">· current: {cfg.api_key_masked}</span>}
                            </label>
                            <input type="password"
                                   placeholder={cfg?.api_key_present ? "Leave empty to keep current" : "sk_test_… or sk_live_…"}
                                   value={form.api_key}
                                   onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                                   className="akm-input mt-1 font-mono text-[12px]" data-testid="channex-api-key" />
                        </div>
                        <div className="sm:col-span-2">
                            <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                                Webhook signing secret
                                {cfg?.webhook_secret_present && <span className="ml-2 text-emerald-600">· set</span>}
                            </label>
                            <input type="password"
                                   placeholder={cfg?.webhook_secret_present ? "Leave empty to keep current" : "any long random string"}
                                   value={form.webhook_secret}
                                   onChange={(e) => setForm({ ...form, webhook_secret: e.target.value })}
                                   className="akm-input mt-1 font-mono text-[12px]" data-testid="channex-webhook-secret" />
                        </div>
                    </div>

                    <div className="mt-4 flex gap-2">
                        <button onClick={saveConfig} disabled={busy}
                                className="akm-btn-primary inline-flex items-center gap-1.5" data-testid="channex-save-cfg">
                            <Save size={13} /> Save
                        </button>
                        <button onClick={testConnection} disabled={busy || !cfg?.api_key_present}
                                className="rounded-md border border-border bg-card px-3 py-1.5 text-[12px] font-semibold inline-flex items-center gap-1.5 hover:bg-muted/60"
                                data-testid="channex-test-cfg">
                            <Zap size={13} /> Test connection
                        </button>
                        <button onClick={pollRevisions} disabled={busy || !cfg?.api_key_present}
                                className="rounded-md border border-border bg-card px-3 py-1.5 text-[12px] font-semibold inline-flex items-center gap-1.5 hover:bg-muted/60">
                            <RefreshCw size={13} /> Poll revision feed
                        </button>
                    </div>

                    <div className="mt-5 rounded-md border border-blue-200 bg-blue-50 p-3 text-[11px] text-blue-900 dark:border-blue-500/30 dark:bg-blue-500/10 dark:text-blue-200">
                        <div className="mb-1 font-semibold">Webhook URL — configure this in your Channex dashboard</div>
                        <div className="flex items-center gap-2">
                            <code className="flex-1 truncate rounded bg-white px-2 py-1 font-mono text-[11px] text-blue-900 dark:bg-blue-900/40 dark:text-blue-100">{webhookUrl}</code>
                            <button onClick={() => { navigator.clipboard.writeText(webhookUrl); toast.success("Copied"); }}
                                    className="rounded-md border border-blue-300 bg-white px-2 py-1 text-[10px] font-semibold text-blue-800 hover:bg-blue-100 dark:bg-blue-900/60 dark:text-blue-200">
                                Copy
                            </button>
                        </div>
                        <div className="mt-2 leading-relaxed">
                            Add custom header <code className="font-mono">X-Channex-Webhook-Secret</code> = your secret above.
                            Subscribe to: <code>booking_new</code>, <code>booking_modification</code>, <code>booking_cancellation</code>.
                        </div>
                    </div>
                    {cfg?.last_health_check && (
                        <div className="mt-3 text-[10px] text-muted-foreground">
                            Last health check: {formatDateTime(cfg.last_health_check)}
                        </div>
                    )}
                    <a href="/downloads/Akomoda_Channex_Certification.pdf" download
                       className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-orange-300 bg-orange-50 px-2.5 py-1.5 text-[11px] font-semibold text-orange-800 hover:bg-orange-100 dark:bg-orange-500/10 dark:text-orange-300"
                       data-testid="channex-download-cert-pdf">
                        ⬇ Download Certification PDF for Channex
                    </a>
                </div>

                {/* Hotel bindings */}
                <div className="akm-card overflow-hidden">
                    <div className="border-b border-border p-4">
                        <div className="text-[13px] font-semibold tracking-tight">Hotel bindings</div>
                        <div className="mt-1 text-[12px] text-muted-foreground">Map each Akomoda hotel to its Channex property.</div>
                    </div>
                    <div className="divide-y divide-border">
                        {hotels.length === 0 ? (
                            <div className="p-6 text-center text-sm text-muted-foreground">No hotels yet</div>
                        ) : hotels.map((h) => (
                            <HotelRow key={h.hotel_id} binding={h} onRefresh={load} />
                        ))}
                    </div>
                </div>

                {/* Audit log — full event stream */}
                <ChannexAuditLog hotels={hotels} />
            </div>
        </>
    );
}

function ChannexAuditLog({ hotels }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterHotel, setFilterHotel] = useState("");
    const [filterType, setFilterType] = useState("");
    const [filterProcessed, setFilterProcessed] = useState("");
    const [open, setOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filterHotel) params.set("hotel_id", filterHotel);
            if (filterType) params.set("event_type", filterType);
            if (filterProcessed !== "") params.set("processed", filterProcessed);
            params.set("limit", "200");
            const { data } = await api.get(`/channex/audit-log?${params.toString()}`);
            setRows(data.events || []);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setLoading(false); }
    };
    useEffect(() => { if (open) load(); }, [open, filterHotel, filterType, filterProcessed]);

    return (
        <div className="akm-card overflow-hidden">
            <button onClick={() => setOpen(!open)} data-testid="channex-audit-toggle"
                    className="flex w-full items-center justify-between p-4 hover:bg-muted/30">
                <div className="flex items-center gap-2">
                    <Activity size={14} className="text-orange-600" />
                    <div className="text-[13px] font-semibold tracking-tight">Full Audit Log · channex_events</div>
                </div>
                <div className="text-[11px] text-muted-foreground">{open ? "Hide" : "Show"} full log</div>
            </button>
            {open && (
                <>
                    <div className="border-t border-border p-3 flex flex-wrap items-center gap-2 bg-muted/20">
                        <select className="akm-input text-[11px]" value={filterHotel}
                                onChange={(e) => setFilterHotel(e.target.value)}
                                data-testid="channex-audit-filter-hotel">
                            <option value="">All hotels</option>
                            {hotels.map((h) => (
                                <option key={h.hotel_id} value={h.hotel_id}>{h.hotel_name || h.hotel_id.slice(0, 8)}</option>
                            ))}
                        </select>
                        <select className="akm-input text-[11px]" value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                                data-testid="channex-audit-filter-type">
                            <option value="">All events</option>
                            <option value="booking_new">booking_new</option>
                            <option value="booking_modification">booking_modification</option>
                            <option value="booking_cancellation">booking_cancellation</option>
                            <option value="booking_no_show">booking_no_show</option>
                        </select>
                        <select className="akm-input text-[11px]" value={filterProcessed}
                                onChange={(e) => setFilterProcessed(e.target.value)}
                                data-testid="channex-audit-filter-processed">
                            <option value="">Any status</option>
                            <option value="true">Processed OK</option>
                            <option value="false">Failed / pending</option>
                        </select>
                        <button onClick={load} disabled={loading}
                                className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 text-[11px]">
                            <RefreshCw size={11} /> Refresh
                        </button>
                        <span className="ml-auto text-[10px] text-muted-foreground">{rows.length} events</span>
                    </div>
                    <div className="max-h-[500px] overflow-auto">
                        {loading ? (
                            <div className="p-6 text-center text-[12px] text-muted-foreground">Loading…</div>
                        ) : rows.length === 0 ? (
                            <div className="p-6 text-center text-[12px] text-muted-foreground">No events match filters</div>
                        ) : (
                            <table className="w-full text-[11px]">
                                <thead className="sticky top-0 bg-muted/50 backdrop-blur">
                                    <tr className="text-left text-[10px] uppercase text-muted-foreground">
                                        <th className="px-3 py-2">Received</th>
                                        <th className="px-3 py-2">Event</th>
                                        <th className="px-3 py-2">Hotel</th>
                                        <th className="px-3 py-2">Booking ID</th>
                                        <th className="px-3 py-2">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {rows.map((r, i) => (
                                        <tr key={i} data-testid={`channex-audit-row-${i}`} className="hover:bg-muted/30">
                                            <td className="px-3 py-1.5 whitespace-nowrap font-mono text-[10px] text-muted-foreground">
                                                {(r.received_at || "").replace("T", " ").slice(0, 19)}
                                            </td>
                                            <td className="px-3 py-1.5">
                                                <code className="rounded bg-orange-50 px-1.5 py-0.5 text-[9px] font-semibold text-orange-700 dark:bg-orange-500/15 dark:text-orange-300">
                                                    {r.event_type}
                                                </code>
                                            </td>
                                            <td className="px-3 py-1.5 truncate max-w-[200px]">{r.hotel_name || <span className="text-muted-foreground">—</span>}</td>
                                            <td className="px-3 py-1.5 font-mono text-[10px] text-muted-foreground truncate max-w-[220px]">{r.booking_id || "—"}</td>
                                            <td className="px-3 py-1.5">
                                                {r.processed ? (
                                                    <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300">
                                                        <CheckCircle2 size={11} /> OK
                                                    </span>
                                                ) : r.error ? (
                                                    <span className="inline-flex items-center gap-1 text-red-700 dark:text-red-300" title={r.error}>
                                                        <XCircle size={11} /> {r.error.slice(0, 40)}
                                                    </span>
                                                ) : (
                                                    <span className="text-muted-foreground">pending</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

function HotelRow({ binding, onRefresh }) {
    const [busy, setBusy] = useState(false);
    const [editing, setEditing] = useState(false);
    const [propId, setPropId] = useState(binding.property_id || "");
    const [createTitle, setCreateTitle] = useState(binding.hotel_name || "");
    const [showCreate, setShowCreate] = useState(false);
    const [showMapping, setShowMapping] = useState(false);
    const [roomCats, setRoomCats] = useState([]);
    const [akmRatePlans, setAkmRatePlans] = useState([]);
    const [rtMap, setRtMap] = useState(binding.room_type_to_category || {});
    const [rpMap, setRpMap] = useState(binding.rate_plan_to_scheme || {});
    const [advanced, setAdvanced] = useState(false);
    const [mappingJson, setMappingJson] = useState(
        JSON.stringify(binding.room_type_to_category || {}, null, 2)
    );
    const [ratePlanJson, setRatePlanJson] = useState(
        JSON.stringify(binding.rate_plan_to_scheme || {}, null, 2)
    );

    const loadMappingSources = async () => {
        try {
            const [rc, rp] = await Promise.all([
                api.get(`/channex/hotels/${binding.hotel_id}/room-categories`),
                api.get(`/rate-plans?hotel_id=${binding.hotel_id}`),
            ]);
            setRoomCats(rc.data || []);
            setAkmRatePlans(rp.data || []);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load mapping sources"); }
    };

    const createProperty = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/channex/hotels/${binding.hotel_id}/create-property`, {
                title: createTitle,
                currency: "EUR", country: "AL", timezone: "Europe/Tirane",
            });
            toast.success(`Property created: ${data.property_id}`);
            setShowCreate(false);
            onRefresh();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const rebind = async () => {
        setBusy(true);
        try {
            await api.put(`/channex/hotels/${binding.hotel_id}/rebind`, { property_id: propId });
            toast.success("Rebound successfully");
            setEditing(false);
            onRefresh();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const sync = async () => {
        setBusy(true);
        try {
            await api.get(`/channex/hotels/${binding.hotel_id}/sync`);
            toast.success("Synced room types + rate plans");
            onRefresh();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const installWebhook = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/channex/hotels/${binding.hotel_id}/install-webhook`);
            toast.success(`Webhook ${data.reused ? "reused" : "installed"}: ${data.event_mask || "booking"}`);
            onRefresh();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const pushAri = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/channex/hotels/${binding.hotel_id}/push-ari`);
            if (data.pushed > 0) toast.success(`Pushed ${data.pushed} availability rows`);
            else toast(data.reason || "Nothing pushed", { icon: "ℹ" });
            onRefresh();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const pushRates = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/channex/hotels/${binding.hotel_id}/push-rates`);
            if (data.pushed > 0) toast.success(`Pushed ${data.pushed} rate rows`);
            else toast(data.reason || "Nothing pushed", { icon: "ℹ" });
            onRefresh();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const fullSync = async () => {
        // Full sync bypasses the delta outbox and pushes 500 days in EXACTLY
        // 2 API calls. Use sparingly — the nightly cron already runs it.
        if (!window.confirm(
            `Full Sync will push the next 500 days of availability + restrictions ` +
            `in 2 API calls. This bypasses the delta queue. Continue?`
        )) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/channex/hotels/${binding.hotel_id}/full-sync?days=500`);
            const a = data.availability, r = data.restrictions;
            toast.success(
                `Full Sync ok · Availability: ${a.rows} rows (task ${a.task_id?.slice(0,8) || "—"}) · ` +
                `Restrictions: ${r.rows} rows (task ${r.task_id?.slice(0,8) || "—"})`
            );
            onRefresh();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const openMapping = () => {
        const next = !showMapping;
        setShowMapping(next);
        if (next) {
            // reset local state from binding
            setRtMap(binding.room_type_to_category || {});
            setRpMap(binding.rate_plan_to_scheme || {});
            setMappingJson(JSON.stringify(binding.room_type_to_category || {}, null, 2));
            setRatePlanJson(JSON.stringify(binding.rate_plan_to_scheme || {}, null, 2));
            loadMappingSources();
        }
    };
    const saveMapping = async () => {
        setBusy(true);
        try {
            const rt = advanced ? JSON.parse(mappingJson || "{}") : rtMap;
            const rp = advanced ? JSON.parse(ratePlanJson || "{}") : rpMap;
            // Strip empties
            const cleanRt = Object.fromEntries(Object.entries(rt).filter(([, v]) => v));
            const cleanRp = Object.fromEntries(Object.entries(rp).filter(([, v]) => v));
            await api.put(`/channex/hotels/${binding.hotel_id}/mapping`,
                          { room_type_to_category: cleanRt, rate_plan_to_scheme: cleanRp });
            toast.success("Mapping saved");
            setShowMapping(false);
            onRefresh();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || String(e)); }
        finally { setBusy(false); }
    };
    const toggleChannel = async (code) => {
        const next = binding.active_channels.includes(code)
            ? binding.active_channels.filter((c) => c !== code)
            : [...binding.active_channels, code];
        setBusy(true);
        try {
            await api.put(`/channex/hotels/${binding.hotel_id}/channels`, { active_channels: next });
            onRefresh();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const rtCount = Object.keys(binding.room_type_map || {}).length;
    const rpCount = Object.keys(binding.rate_plan_map || {}).length;

    return (
        <div className="p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <div className="text-[14px] font-semibold">{binding.hotel_name || binding.hotel_id.slice(0, 8)}</div>
                        {binding.property_id ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                                <CheckCircle2 size={10} /> Bound
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600 dark:bg-slate-500/20 dark:text-slate-300">
                                Not bound
                            </span>
                        )}
                        {binding.env && (
                            <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
                                {binding.env}
                            </span>
                        )}
                    </div>
                    <div className="mt-1 text-[11px] text-muted-foreground">
                        {binding.property_id ? (
                            <>
                                <code className="font-mono">{binding.property_id}</code>
                                {" · "}Room types: {rtCount} · Rate plans: {rpCount}
                                {binding.synced_at && <> · synced {formatDateTime(binding.synced_at)}</>}
                            </>
                        ) : "No Channex property assigned"}
                    </div>
                    {binding.last_error && (
                        <div className="mt-1 inline-flex items-center gap-1 rounded bg-red-50 px-2 py-0.5 text-[10px] font-medium text-red-700 dark:bg-red-500/10 dark:text-red-300">
                            <AlertCircle size={10} /> {binding.last_error.slice(0, 100)}
                        </div>
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-1.5">
                    {!binding.property_id && (
                        <button onClick={() => setShowCreate(true)} disabled={busy}
                                className="inline-flex items-center gap-1 rounded-md bg-orange-500 px-2.5 py-1.5 text-[11px] font-semibold text-white hover:bg-orange-600">
                            <KeyRound size={11} /> Create property
                        </button>
                    )}
                    <button onClick={() => setEditing((e) => !e)} disabled={busy}
                            className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2.5 py-1.5 text-[11px] font-medium hover:bg-muted/60">
                        <Link2 size={11} /> {binding.property_id ? "Rebind" : "Bind existing"}
                    </button>
                    {binding.property_id && (
                        <button onClick={sync} disabled={busy}
                                className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2.5 py-1.5 text-[11px] font-medium hover:bg-muted/60">
                            <RefreshCw size={11} /> Sync
                        </button>
                    )}
                    {binding.property_id && (
                        <button onClick={installWebhook} disabled={busy}
                                className="inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-2.5 py-1.5 text-[11px] font-semibold text-emerald-800 hover:bg-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-300"
                                data-testid={`channex-install-webhook-${binding.hotel_id}`}>
                            <Zap size={11} /> Install webhook
                        </button>
                    )}
                    {binding.property_id && (
                        <button onClick={openMapping} disabled={busy}
                                data-testid={`channex-mapping-btn-${binding.hotel_id}`}
                                className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2.5 py-1.5 text-[11px] font-medium hover:bg-muted/60">
                            <Link2 size={11} /> Mapping
                        </button>
                    )}
                    {binding.property_id && (binding.room_type_to_category && Object.keys(binding.room_type_to_category).length > 0) && (
                        <button onClick={pushAri} disabled={busy}
                                data-testid={`channex-push-ari-${binding.hotel_id}`}
                                className="inline-flex items-center gap-1 rounded-md bg-blue-500 px-2.5 py-1.5 text-[11px] font-semibold text-white hover:bg-blue-600">
                            <RefreshCw size={11} /> Push ARI (365 days)
                        </button>
                    )}
                    {binding.property_id && (binding.rate_plan_to_scheme && Object.keys(binding.rate_plan_to_scheme).length > 0) && (
                        <button onClick={pushRates} disabled={busy}
                                data-testid={`channex-push-rates-${binding.hotel_id}`}
                                className="inline-flex items-center gap-1 rounded-md bg-indigo-500 px-2.5 py-1.5 text-[11px] font-semibold text-white hover:bg-indigo-600">
                            <DollarSign size={11} /> Push Rates (365 days)
                        </button>
                    )}
                    {binding.property_id && (
                        <button onClick={fullSync} disabled={busy}
                                data-testid={`channex-full-sync-${binding.hotel_id}`}
                                className="inline-flex items-center gap-1 rounded-md bg-orange-500 px-2.5 py-1.5 text-[11px] font-semibold text-white shadow-sm hover:bg-orange-600">
                            <RefreshCw size={11} /> Full Sync (500 days · 2 calls)
                        </button>
                    )}
                </div>
            </div>

            {showCreate && (
                <div className="mt-3 rounded-md border border-border bg-muted/30 p-3">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Create new Channex property</div>
                    <div className="mt-2 flex items-center gap-2">
                        <input value={createTitle} onChange={(e) => setCreateTitle(e.target.value)}
                               placeholder="Hotel title" className="akm-input flex-1 text-[12px]" />
                        <button onClick={createProperty} disabled={busy || !createTitle}
                                className="akm-btn-primary text-[11px]">Create</button>
                        <button onClick={() => setShowCreate(false)} className="rounded-md border border-border bg-card px-2 py-1 text-[11px]">Cancel</button>
                    </div>
                </div>
            )}

            {editing && (
                <div className="mt-3 rounded-md border border-border bg-muted/30 p-3">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Rebind property_id (useful for staging resets)</div>
                    <div className="mt-2 flex items-center gap-2">
                        <input value={propId} onChange={(e) => setPropId(e.target.value)}
                               placeholder="e.g. 8a7b6c…" className="akm-input flex-1 font-mono text-[12px]" />
                        <button onClick={rebind} disabled={busy || !propId}
                                className="akm-btn-primary text-[11px]">Rebind</button>
                        <button onClick={() => setEditing(false)} className="rounded-md border border-border bg-card px-2 py-1 text-[11px]">Cancel</button>
                    </div>
                </div>
            )}

            {showMapping && (
                <div className="mt-3 rounded-md border border-border bg-muted/30 p-3 space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                            Mappings (Channex → Akomoda)
                        </div>
                        <div className="flex items-center gap-3">
                            <label className="inline-flex items-center gap-1.5 text-[10px] text-muted-foreground">
                                <input type="checkbox" checked={advanced}
                                       onChange={(e) => setAdvanced(e.target.checked)} />
                                Advanced (JSON)
                            </label>
                            <button onClick={() => setShowMapping(false)} className="text-[10px] text-muted-foreground">Close</button>
                        </div>
                    </div>

                    {!advanced ? (
                        <>
                            {/* Room types dropdown mapping */}
                            <div>
                                <div className="text-[10px] uppercase text-muted-foreground mb-1.5">Room types</div>
                                {Object.keys(binding.room_type_map || {}).length === 0 ? (
                                    <div className="rounded border border-dashed border-border p-3 text-[11px] text-muted-foreground">
                                        No room types synced yet. Click <b>Sync</b> first.
                                    </div>
                                ) : (
                                    <div className="space-y-1">
                                        {Object.entries(binding.room_type_map || {}).map(([cxId, title]) => (
                                            <div key={cxId} className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
                                                <div className="truncate rounded border border-border bg-card px-2 py-1.5 text-[11px]">
                                                    <b>{title}</b>
                                                    <span className="ml-2 font-mono text-[9px] text-muted-foreground">{cxId.slice(0, 8)}</span>
                                                </div>
                                                <div className="text-center text-muted-foreground">→</div>
                                                <select value={rtMap[cxId] || ""}
                                                        onChange={(e) => setRtMap({ ...rtMap, [cxId]: e.target.value })}
                                                        className="akm-input text-[11px]"
                                                        data-testid={`channex-map-rt-${cxId.slice(0, 6)}`}>
                                                    <option value="">— select Akomoda category —</option>
                                                    {roomCats.map((rc) => (
                                                        <option key={rc.id} value={rc.id}>{rc.name || rc.code || rc.id.slice(0, 8)}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Rate plans dropdown mapping */}
                            <div>
                                <div className="text-[10px] uppercase text-muted-foreground mb-1.5">Rate plans</div>
                                {Object.keys(binding.rate_plan_map || {}).length === 0 ? (
                                    <div className="rounded border border-dashed border-border p-3 text-[11px] text-muted-foreground">
                                        No rate plans synced yet. Click <b>Sync</b> first.
                                    </div>
                                ) : akmRatePlans.length === 0 ? (
                                    <div className="rounded border border-dashed border-amber-300 bg-amber-50 p-3 text-[11px] text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
                                        This hotel has no native Akomoda rate plans yet. Create some under
                                        <b> Rate Plans</b> in the hotel workspace first, then reopen this mapping.
                                    </div>
                                ) : (
                                    <div className="space-y-1">
                                        {Object.entries(binding.rate_plan_map || {}).map(([cxId, title]) => (
                                            <div key={cxId} className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
                                                <div className="truncate rounded border border-border bg-card px-2 py-1.5 text-[11px]">
                                                    <b>{title}</b>
                                                    <span className="ml-2 font-mono text-[9px] text-muted-foreground">{cxId.slice(0, 8)}</span>
                                                </div>
                                                <div className="text-center text-muted-foreground">→</div>
                                                <select value={rpMap[cxId] || ""}
                                                        onChange={(e) => setRpMap({ ...rpMap, [cxId]: e.target.value })}
                                                        className="akm-input text-[11px]"
                                                        data-testid={`channex-map-rp-${cxId.slice(0, 6)}`}>
                                                    <option value="">— select Akomoda rate plan —</option>
                                                    {akmRatePlans.map((rp) => (
                                                        <option key={rp.id} value={rp.id}>
                                                            {rp.code} · {rp.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            <div>
                                <div className="text-[10px] uppercase text-muted-foreground mb-1">Room types (JSON)</div>
                                <div className="grid gap-3 md:grid-cols-2">
                                    <div className="rounded border border-border bg-card p-2 text-[11px] font-mono max-h-32 overflow-auto">
                                        {Object.entries(binding.room_type_map || {}).map(([id, title]) => (
                                            <div key={id} className="flex justify-between gap-2"><span className="truncate">{title}</span><span className="text-muted-foreground">{id.slice(0,8)}</span></div>
                                        ))}
                                    </div>
                                    <textarea value={mappingJson} onChange={(e) => setMappingJson(e.target.value)}
                                              className="akm-input h-32 w-full font-mono text-[11px]"
                                              placeholder='{"channex_room_type_id": "akomoda_room_type_id"}' />
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase text-muted-foreground mb-1">Rate plans (JSON)</div>
                                <div className="grid gap-3 md:grid-cols-2">
                                    <div className="rounded border border-border bg-card p-2 text-[11px] font-mono max-h-32 overflow-auto">
                                        {Object.entries(binding.rate_plan_map || {}).map(([id, title]) => (
                                            <div key={id} className="flex justify-between gap-2"><span className="truncate">{title}</span><span className="text-muted-foreground">{id.slice(0,8)}</span></div>
                                        ))}
                                    </div>
                                    <textarea value={ratePlanJson} onChange={(e) => setRatePlanJson(e.target.value)}
                                              className="akm-input h-32 w-full font-mono text-[11px]"
                                              placeholder='{"channex_rate_plan_id": "akomoda_rate_plan_id"}' />
                                </div>
                            </div>
                        </>
                    )}

                    <button onClick={saveMapping} disabled={busy} className="akm-btn-primary text-[11px]"
                            data-testid="channex-save-mapping">Save mappings</button>
                </div>
            )}

            {/* Channel toggles */}
            {binding.property_id && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                    {Object.entries(OTA_META).map(([code, meta]) => {
                        const active = binding.active_channels.includes(code);
                        return (
                            <button key={code} onClick={() => toggleChannel(code)} disabled={busy}
                                    className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors ${
                                        active
                                            ? "bg-emerald-500 text-white shadow-sm"
                                            : "border border-border bg-card text-muted-foreground hover:bg-muted/40"
                                    }`}
                                    data-testid={`channex-toggle-${code}`}>
                                <span className={`h-1.5 w-1.5 rounded-full ${active ? "bg-white" : meta.color}`} />
                                {meta.label}
                                {active ? <CheckCircle2 size={10} /> : <XCircle size={10} className="opacity-40" />}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

const TONE = {
    blue:    "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-500/30 dark:bg-blue-500/10 dark:text-blue-300",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300",
    red:     "border-red-200 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300",
    amber:   "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300",
    slate:   "border-border bg-muted/40 text-muted-foreground",
};
function StatCard({ label, value, tone = "slate" }) {
    return (
        <div className={`rounded-md border p-3 ${TONE[tone]}`}>
            <div className="text-[10px] font-semibold uppercase tracking-wider opacity-80">{label}</div>
            <div className="mt-1 text-xl font-semibold tracking-tight">{value ?? "—"}</div>
        </div>
    );
}


function Row({ label, value }) {
    return (
        <div className="flex items-center justify-between gap-3">
            <span className="text-slate-500">{label}</span>
            <span className="font-semibold text-slate-900 text-right">{value ?? "—"}</span>
        </div>
    );
}



// ─────────────────────────────────────────────────────────────
// P1 — Outbox observability widget
// Polls /api/channex/outbox/stats every 5s so ops can watch the delta-push
// queue drain in real time. Silent no-op if endpoint is unavailable.
// ─────────────────────────────────────────────────────────────
function OutboxDashboard() {
    const [stats, setStats] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const { data } = await api.get("/channex/outbox/stats");
                if (!cancelled) { setStats(data); setError(null); }
            } catch (e) {
                if (!cancelled) setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load outbox stats");
            }
        };
        load();
        const t = setInterval(load, 5000);      // live poll
        return () => { cancelled = true; clearInterval(t); };
    }, []);

    if (error) {
        return (
            <div className="akm-card p-4 text-[12px] text-red-600" data-testid="outbox-dashboard-error">
                Outbox stats unavailable: {error}
            </div>
        );
    }
    if (!stats) return null;

    const hotelRows = Object.entries(stats.outbox_by_hotel || {});
    const running = !!stats.worker_running;

    return (
        <div className="akm-card p-5" data-testid="outbox-dashboard">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                        <Package size={12} /> Delta Push Outbox
                    </div>
                    <h3 className="mt-1 text-base font-semibold text-slate-900">
                        Channel Manager Sync Queue
                    </h3>
                    <p className="mt-0.5 text-[11.5px] text-muted-foreground">
                        Coalesces dirty date-ranges and pushes them via the rate-limited
                        Channex queue every {stats.flush_interval_seconds}s. Retries 429s
                        with exponential backoff.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <MuteControls stats={stats} onChange={() => {
                        // Trigger refresh immediately by reloading stats
                        api.get("/channex/outbox/stats").then(r => setStats(r.data)).catch(() => {});
                    }} />
                    <span
                        data-testid="outbox-worker-status"
                        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10.5px] font-semibold ${
                            running
                                ? "bg-emerald-50 text-emerald-700"
                                : "bg-amber-50 text-amber-700"
                        }`}
                    >
                        <span className={`h-1.5 w-1.5 rounded-full ${running ? "bg-emerald-500 animate-pulse" : "bg-amber-500"}`} />
                        {running ? "Worker running" : "Worker stopped"}
                    </span>
                </div>
            </div>

            {/* Muted banner — shown when alerts are suppressed during maintenance */}
            {stats.alert?.muted_until && new Date(stats.alert.muted_until) > new Date() && (
                <div
                    data-testid="outbox-muted-banner"
                    className="mt-3 flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] text-slate-700"
                >
                    <Bell size={14} className="mt-0.5 shrink-0 text-slate-500" />
                    <div className="min-w-0 flex-1">
                        <div className="font-semibold">Alerts muted until {formatDateTime(stats.alert.muted_until)}</div>
                        <div className="text-[11px] text-slate-600">
                            Stuck-outbox alerts, escalations, and incident records are suppressed
                            during this window. Backlog is still being processed and pushed to Channex.
                            {stats.alert.muted_by && <> Muted by <span className="font-mono">{stats.alert.muted_by}</span>.</>}
                            {stats.alert.muted_reason && <> Reason: <em>{stats.alert.muted_reason}</em></>}
                        </div>
                    </div>
                </div>
            )}

            {/* Stuck-outbox alert banner (P1 monitoring) — hidden while muted */}
            {stats.alert?.active && !(stats.alert?.muted_until && new Date(stats.alert.muted_until) > new Date()) && (
                <div
                    data-testid="outbox-stuck-alert"
                    className="mt-3 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-800"
                >
                    <AlertCircle size={14} className="mt-0.5 shrink-0" />
                    <div className="min-w-0 flex-1">
                        <div className="font-semibold">Outbox stuck: {stats.outbox_rows} rows</div>
                        <div className="text-[11px] text-red-700">
                            Above the {stats.alert.high_watermark}-row threshold for &gt;
                            {stats.alert.stuck_minutes} minutes. Super admins have been
                            notified via in-app + email. Auto-clears below {stats.alert.low_watermark} rows.
                        </div>
                        {stats.alert.escalated_at ? (
                            <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[10.5px] font-medium text-red-900" data-testid="outbox-escalation-active">
                                <span className="rounded-full bg-red-100 px-2 py-0.5">ESCALATED</span>
                                {(stats.alert.escalation?.delivered || []).map((ch) => (
                                    <span key={ch} className="rounded-full bg-red-200/60 px-2 py-0.5 uppercase tracking-wide">
                                        {ch}
                                    </span>
                                ))}
                                <span className="text-red-700 font-normal">
                                    at {formatDateTime(stats.alert.escalated_at)}
                                </span>
                                {stats.alert.escalation?.runbook_url && (
                                    <a
                                        href={stats.alert.escalation.runbook_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="ml-1 rounded-full bg-white/70 px-2 py-0.5 text-red-800 underline"
                                        data-testid="outbox-escalation-runbook"
                                    >
                                        Open runbook →
                                    </a>
                                )}
                            </div>
                        ) : (
                            <div className="mt-1.5 text-[10.5px] text-red-700" data-testid="outbox-escalation-pending">
                                Will escalate to Slack/PagerDuty if still stuck at {stats.alert.escalate_minutes}m after initial alert.
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Counters */}
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <MiniStat label="Dirty rows" value={stats.outbox_rows} tone={stats.outbox_rows > 0 ? "orange" : "slate"} testid="outbox-rows" />
                <MiniStat label="Hotels queued" value={stats.outbox_hotels} tone={stats.outbox_hotels > 0 ? "amber" : "slate"} testid="outbox-hotels" />
                <MiniStat label="Recent pushes" value={stats.recent_pushes?.length ?? 0} tone="blue" testid="outbox-recent-pushes" />
                <MiniStat label="Flush every" value={`${stats.flush_interval_seconds}s`} tone="slate" testid="outbox-interval" />
            </div>

            {/* Per-hotel breakdown */}
            {hotelRows.length > 0 && (
                <div className="mt-4">
                    <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                        Dirty rows by hotel
                    </div>
                    <div className="divide-y divide-border rounded-md border border-border bg-card text-[12px]">
                        {hotelRows.map(([hid, c]) => (
                            <div key={hid} className="flex items-center justify-between px-3 py-2" data-testid={`outbox-hotel-${hid}`}>
                                <span className="font-mono text-[11px] text-slate-600">{hid.slice(0, 8)}…</span>
                                <div className="flex items-center gap-3">
                                    <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700">
                                        {c.availability ?? 0} avail
                                    </span>
                                    <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-medium text-indigo-700">
                                        {c.restrictions ?? 0} restr
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Recent pushes */}
            {stats.recent_pushes && stats.recent_pushes.length > 0 && (
                <div className="mt-4">
                    <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                        Recent pushes ({stats.recent_pushes.length})
                    </div>
                    <div className="max-h-56 overflow-y-auto divide-y divide-border rounded-md border border-border bg-card text-[11.5px]">
                        {stats.recent_pushes.slice(0, 15).map((p) => (
                            <div key={p.id} className="flex items-center justify-between gap-3 px-3 py-1.5" data-testid={`push-log-${p.id}`}>
                                <div className="flex min-w-0 items-center gap-2">
                                    <span className={`inline-block h-1.5 w-1.5 rounded-full ${
                                        p.error ? "bg-red-500" :
                                        p.source === "full_sync" ? "bg-orange-500" : "bg-emerald-500"
                                    }`} />
                                    <span className={`truncate rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                                        p.kind === "availability" ? "bg-blue-50 text-blue-700" : "bg-indigo-50 text-indigo-700"
                                    }`}>{p.kind}</span>
                                    <span className="font-mono text-[10.5px] text-slate-500">{p.hotel_id?.slice(0, 8) || "—"}…</span>
                                    <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">{p.source}</span>
                                </div>
                                <div className="flex shrink-0 items-center gap-2">
                                    <span className="text-slate-600">{p.rows_count} rows</span>
                                    {p.task_id && (
                                        <span className="font-mono text-[10px] text-slate-400" title={p.task_id}>
                                            {p.task_id.slice(0, 8)}
                                        </span>
                                    )}
                                    {p.error && (
                                        <span className="rounded-full bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-700" title={p.error}>err</span>
                                    )}
                                    <span className="text-[10px] text-slate-400">{formatDateTime(p.sent_at)}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {hotelRows.length === 0 && (
                <div className="mt-4 rounded-md border border-dashed border-border bg-muted/30 py-3 text-center text-[11.5px] text-muted-foreground">
                    Outbox is empty — everything is synced to Channex.
                </div>
            )}
        </div>
    );
}

function MiniStat({ label, value, tone = "slate", testid }) {
    const toneClasses = {
        slate:  "text-slate-900",
        orange: "text-orange-600",
        amber:  "text-amber-600",
        blue:   "text-blue-600",
        emerald:"text-emerald-600",
    };
    return (
        <div className="rounded-lg border border-border bg-card p-3" data-testid={testid}>
            <div className="text-[10.5px] font-medium uppercase tracking-wider text-slate-500">{label}</div>
            <div className={`mt-0.5 text-lg font-semibold ${toneClasses[tone] || toneClasses.slate}`}>
                {value ?? "—"}
            </div>
        </div>
    );
}


// ─────────────────────────────────────────────────────────────
// Escalation config card — Slack + PagerDuty targets for stuck alerts
// ─────────────────────────────────────────────────────────────
function EscalationConfigCard() {
    const [cfg, setCfg] = useState(null);
    const [slack, setSlack] = useState("");
    const [pd, setPd] = useState("");
    const [runbook, setRunbook] = useState("");
    const [enabled, setEnabled] = useState(true);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/channex/escalation/config");
            setCfg(data);
            setEnabled(data.enabled);
            setRunbook(data.runbook_url || "");
        } catch (e) { /* silent */ }
    };
    useEffect(() => { load(); }, []);

    if (!cfg) return null;

    const save = async () => {
        setBusy(true);
        try {
            const payload = { enabled, runbook_url: runbook.trim() };
            if (slack.trim()) payload.slack_webhook_url = slack.trim();
            if (pd.trim()) payload.pagerduty_routing_key = pd.trim();
            await api.put("/channex/escalation/config", payload);
            toast.success("Escalation config saved");
            setSlack(""); setPd("");
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
        } finally { setBusy(false); }
    };

    const test = async (channel) => {
        setBusy(true);
        try {
            const { data } = await api.post(`/channex/escalation/test?channel=${channel}`);
            if (data.ok) toast.success(`Test sent to ${channel}`);
            else toast.error(data.error || `Test to ${channel} failed`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Test failed");
        } finally { setBusy(false); }
    };

    return (
        <div className="akm-card p-5" data-testid="escalation-config">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                        <Bell size={12} /> Alert Escalation
                    </div>
                    <h3 className="mt-1 text-base font-semibold text-slate-900">
                        Slack + PagerDuty on stuck alerts &gt; {cfg.escalate_after_minutes}m
                    </h3>
                    <p className="mt-0.5 text-[11.5px] text-muted-foreground">
                        When the outbox stays stuck beyond the initial in-app/email alert
                        for {cfg.escalate_after_minutes} minutes, an escalation is fired
                        exactly once to your Slack channel and/or PagerDuty service.
                        Auto-resolves when the queue drains.
                    </p>
                </div>
                <label className="inline-flex cursor-pointer items-center gap-2 text-[11.5px]" data-testid="escalation-enabled-toggle">
                    <input
                        type="checkbox"
                        checked={enabled}
                        onChange={(e) => setEnabled(e.target.checked)}
                    />
                    <span className={enabled ? "text-emerald-700 font-semibold" : "text-slate-500"}>
                        {enabled ? "Enabled" : "Disabled"}
                    </span>
                </label>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
                {/* Slack */}
                <div className="rounded-lg border border-border bg-card p-4">
                    <div className="flex items-center justify-between">
                        <div className="text-[12px] font-semibold text-slate-900">Slack webhook</div>
                        {cfg.slack_configured ? (
                            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">Configured</span>
                        ) : (
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">Not set</span>
                        )}
                    </div>
                    {cfg.slack_configured && (
                        <div className="mt-1 font-mono text-[10.5px] text-slate-500 truncate" data-testid="slack-webhook-masked">
                            {cfg.slack_webhook_masked}
                        </div>
                    )}
                    <input
                        type="text"
                        value={slack}
                        onChange={(e) => setSlack(e.target.value)}
                        placeholder="https://hooks.slack.com/services/T00/B00/…"
                        className="mt-2 w-full rounded-md border border-border bg-white px-2.5 py-1.5 text-[12px] font-mono"
                        data-testid="slack-webhook-input"
                    />
                    <button
                        onClick={() => test("slack")}
                        disabled={busy || !cfg.slack_configured}
                        className="akm-btn-ghost mt-2 text-[11px] w-full"
                        data-testid="slack-test-button"
                    >
                        <Send size={11} /> Send test to Slack
                    </button>
                </div>

                {/* PagerDuty */}
                <div className="rounded-lg border border-border bg-card p-4">
                    <div className="flex items-center justify-between">
                        <div className="text-[12px] font-semibold text-slate-900">PagerDuty routing key</div>
                        {cfg.pagerduty_configured ? (
                            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">Configured</span>
                        ) : (
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">Not set</span>
                        )}
                    </div>
                    {cfg.pagerduty_configured && (
                        <div className="mt-1 font-mono text-[10.5px] text-slate-500" data-testid="pagerduty-masked">
                            {cfg.pagerduty_routing_key_masked}
                        </div>
                    )}
                    <input
                        type="text"
                        value={pd}
                        onChange={(e) => setPd(e.target.value)}
                        placeholder="Events API v2 integration key (32 chars)"
                        className="mt-2 w-full rounded-md border border-border bg-white px-2.5 py-1.5 text-[12px] font-mono"
                        data-testid="pagerduty-key-input"
                    />
                    <button
                        onClick={() => test("pagerduty")}
                        disabled={busy || !cfg.pagerduty_configured}
                        className="akm-btn-ghost mt-2 text-[11px] w-full"
                        data-testid="pagerduty-test-button"
                    >
                        <Send size={11} /> Trigger test PagerDuty incident
                    </button>
                </div>
            </div>

            <div className="mt-4 rounded-lg border border-border bg-card p-4" data-testid="runbook-url-section">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="text-[12px] font-semibold text-slate-900">Runbook URL</div>
                        <p className="mt-0.5 text-[11px] text-muted-foreground">
                            Attached to every escalated PagerDuty incident (in the incident&nbsp;
                            <span className="font-mono">links</span> block) and as a Slack button so
                            on-call sees fix steps the moment they open the page.
                        </p>
                    </div>
                    {cfg.runbook_url && (
                        <a
                            href={cfg.runbook_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[11px] font-medium text-orange-600 hover:underline"
                            data-testid="runbook-open-link"
                        >
                            Open →
                        </a>
                    )}
                </div>
                <input
                    type="url"
                    value={runbook}
                    onChange={(e) => setRunbook(e.target.value)}
                    placeholder="https://docs.akomoda.co/runbooks/channex-outbox-stuck"
                    className="mt-2 w-full rounded-md border border-border bg-white px-2.5 py-1.5 text-[12px] font-mono"
                    data-testid="runbook-url-input"
                />
            </div>

            <div className="mt-4 flex items-center justify-end gap-2">
                <button
                    onClick={save}
                    disabled={busy}
                    className="akm-btn-primary text-[12px]"
                    data-testid="escalation-save-button"
                >
                    <Save size={12} /> {busy ? "Saving…" : "Save escalation config"}
                </button>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// Incident Timeline — chronological list of stuck-outbox episodes.
// Ops can spot patterns like "every incident escalates" (chronic problem)
// at a glance without digging through logs.
// ─────────────────────────────────────────────────────────────
function IncidentTimeline() {
    const [rows, setRows] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const { data } = await api.get("/channex/incidents?limit=15");
                if (!cancelled) { setRows(data.incidents || []); setError(null); }
            } catch (e) {
                if (!cancelled) setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load incidents");
            }
        };
        load();
        const t = setInterval(load, 10000);
        return () => { cancelled = true; clearInterval(t); };
    }, []);

    if (error) {
        return (
            <div className="akm-card p-4 text-[12px] text-red-600" data-testid="incident-timeline-error">
                Incidents unavailable: {error}
            </div>
        );
    }
    if (!rows) return null;

    // Aggregate: how many incidents in the last 24h, how many escalated
    const now = Date.now();
    const last24h = rows.filter(r => now - new Date(r.alerted_at).getTime() < 24 * 3600 * 1000);
    const escalated24h = last24h.filter(r => r.escalated_at).length;

    return (
        <div className="akm-card p-5" data-testid="incident-timeline">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                        <History size={12} /> Incident History
                    </div>
                    <h3 className="mt-1 text-base font-semibold text-slate-900">
                        Last {rows.length} stuck-outbox incidents
                    </h3>
                    <p className="mt-0.5 text-[11.5px] text-muted-foreground">
                        Every time the delta-push outbox stays above the {last24h.length > 0 ? "high" : "stuck"} watermark
                        long enough to trip the alert, we record it here so patterns are visible instantly.
                    </p>
                </div>
                <div className="flex items-center gap-2 text-[11px]" data-testid="incident-timeline-summary">
                    <span className="rounded-full bg-slate-100 px-2 py-1 font-medium text-slate-700">
                        {last24h.length} in 24h
                    </span>
                    <span className={`rounded-full px-2 py-1 font-medium ${
                        escalated24h > 0 ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"
                    }`}>
                        {escalated24h} escalated
                    </span>
                </div>
            </div>

            {rows.length === 0 ? (
                <div className="mt-4 rounded-md border border-dashed border-border bg-muted/30 py-6 text-center text-[12px] text-muted-foreground" data-testid="incident-timeline-empty">
                    No stuck-outbox incidents recorded. The delta-push worker has been steady.
                </div>
            ) : (
                <ol className="mt-4 space-y-2" data-testid="incident-timeline-list">
                    {rows.map((r) => <IncidentRow key={r.id} row={r} />)}
                </ol>
            )}
        </div>
    );
}

function IncidentRow({ row }) {
    const active = row.status === "active";
    const escalated = !!row.escalated_at;
    const dur = row.duration_seconds ?? 0;
    const durStr = dur >= 3600
        ? `${Math.floor(dur / 3600)}h ${Math.floor((dur % 3600) / 60)}m`
        : `${Math.floor(dur / 60)}m ${dur % 60}s`;

    return (
        <li
            className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 text-[12px] ${
                active
                    ? escalated
                        ? "border-red-200 bg-red-50/70"
                        : "border-amber-200 bg-amber-50/60"
                    : "border-border bg-card"
            }`}
            data-testid={`incident-row-${row.id}`}
        >
            {/* Status dot */}
            <span
                className={`mt-1 inline-block h-2.5 w-2.5 shrink-0 rounded-full ${
                    active
                        ? escalated
                            ? "bg-red-500 animate-pulse"
                            : "bg-amber-500 animate-pulse"
                        : "bg-emerald-500"
                }`}
                aria-hidden
            />

            <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5 font-semibold text-slate-900">
                    <span>{active ? "ACTIVE" : "Resolved"}</span>
                    <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-700">
                        peak {row.peak_rows} rows
                    </span>
                    {escalated && (
                        <span className="rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-800" data-testid="incident-escalated-badge">
                            escalated
                        </span>
                    )}
                    {escalated && (row.escalation?.delivered || []).map((ch) => (
                        <span key={ch} className="rounded-full bg-red-200/50 px-1.5 py-0.5 text-[10px] font-medium uppercase text-red-900">
                            {ch}
                        </span>
                    ))}
                </div>

                <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-0.5 text-[11px] text-slate-600">
                    <span className="inline-flex items-center gap-1">
                        <Clock size={10} className="text-slate-400" />
                        {formatDateTime(row.alerted_at)}
                    </span>
                    <span>
                        Duration: <span className="font-medium text-slate-800">{durStr}</span>
                    </span>
                    {row.escalated_at && (
                        <span>
                            Escalated at <span className="font-mono text-[10.5px] text-red-700">{formatDateTime(row.escalated_at)}</span>
                        </span>
                    )}
                    {row.escalation?.runbook_url && (
                        <a
                            href={row.escalation.runbook_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-orange-600 underline"
                            data-testid={`incident-runbook-${row.id}`}
                        >
                            Runbook →
                        </a>
                    )}
                </div>
            </div>
        </li>
    );
}


// ─────────────────────────────────────────────────────────────
// Mute controls — silence outbox stuck alerts for a maintenance window.
// ─────────────────────────────────────────────────────────────
function MuteControls({ stats, onChange }) {
    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const muted = !!(stats.alert?.muted_until && new Date(stats.alert.muted_until) > new Date());

    const mute = async (minutes) => {
        setBusy(true);
        try {
            await api.post("/channex/outbox/mute", { minutes, reason: reason.trim() || undefined });
            toast.success(`Alerts muted for ${minutes} min`);
            setOpen(false); setReason("");
            onChange?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Mute failed");
        } finally { setBusy(false); }
    };

    const unmute = async () => {
        setBusy(true);
        try {
            await api.post("/channex/outbox/unmute");
            toast.success("Alerts un-muted");
            onChange?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Unmute failed");
        } finally { setBusy(false); }
    };

    if (muted) {
        return (
            <button
                onClick={unmute}
                disabled={busy}
                data-testid="outbox-unmute-button"
                className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-[10.5px] font-semibold text-slate-700 hover:bg-slate-200"
                title="Resume alerts immediately"
            >
                <Bell size={11} /> Un-mute alerts
            </button>
        );
    }

    return (
        <div className="relative">
            <button
                onClick={() => setOpen(!open)}
                data-testid="outbox-mute-toggle"
                className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 px-2.5 py-1 text-[10.5px] font-semibold text-slate-600 hover:bg-slate-100"
                title="Silence alerts during a maintenance window"
            >
                <Bell size={11} /> Silence…
            </button>
            {open && (
                <div
                    data-testid="outbox-mute-menu"
                    className="absolute right-0 top-full z-10 mt-1 w-64 rounded-md border border-border bg-white p-3 shadow-lg"
                >
                    <div className="text-[11px] font-semibold text-slate-900">
                        Silence stuck-outbox alerts
                    </div>
                    <p className="mt-0.5 text-[10.5px] text-muted-foreground">
                        The worker keeps pushing to Channex. Only in-app / email
                        / Slack / PD alerts are suppressed during the window.
                    </p>
                    <input
                        type="text"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Reason (optional) — e.g. rate reload"
                        className="mt-2 w-full rounded-md border border-border bg-white px-2 py-1 text-[11px]"
                        data-testid="outbox-mute-reason"
                    />
                    <div className="mt-2 grid grid-cols-3 gap-1.5">
                        {[30, 60, 120].map((m) => (
                            <button
                                key={m}
                                onClick={() => mute(m)}
                                disabled={busy}
                                data-testid={`outbox-mute-${m}m`}
                                className="rounded-md border border-border bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                            >
                                {m}m
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => setOpen(false)}
                        className="mt-2 w-full text-[10.5px] text-muted-foreground hover:text-foreground"
                    >
                        Cancel
                    </button>
                </div>
            )}
        </div>
    );
}

